import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { pricingService } from '../../_services/pricing.service';
import { setUsage } from './usageStore';
import {
    isSolo, isUpgraded, planSummary, upgradeIncludes, upgradePriceLine, upgradePricePence, FREE_BAILEY_QUESTIONS_FALLBACK,
    upgradeChargeLine, upgradeErrorCopy, isSelfEmployedView, ctaState, unavailableCopy, baileyPlusCap, receptionistState, BAILEY_PLUS } from './usageRules';
import { formatPrice, shortDate } from './money';
import { Tick, Dash } from './PricingBits';
import { hapticError, hapticSuccess, hapticTick } from '../../_utils/haptics';
import './planUsage.css';

/**
 * Beu vs Bailey Plus (pricing 2026-10-07), for THIS business (Solo or per location), then
 * confirm → POST start → done. When they already have it: what it includes,
 * and cancel behind a confirmation.
 *
 * Only the owner changes the plan; anyone else sees the comparison and who
 * to ask.
 */
function Item({ on = true, children }) {
    return <li className={on ? '' : 'is-off'}>{on ? <Tick /> : <Dash />}<span>{children}</span></li>;
}

export function compareRows(usage) {
    const inc = upgradeIncludes(usage);
    const cap = baileyPlusCap(usage).toLocaleString('en-GB');
    if (inc.selfEmployed) {
        // A self-employed stylist's own Bailey Plus: Bailey for them, nothing of the salon's.
        const free = (usage && usage.bailey && usage.bailey.allowance) || FREE_BAILEY_QUESTIONS_FALLBACK;
        return {
            beu: [
                { on: true, text: `Bailey, ${free} questions a month` },
                { on: true, text: 'Your clients and your money stay yours' },
            ],
            plus: [
                { on: true, text: `Bailey, up to ${cap} questions a month, just for you` },
                { on: true, text: 'Help with your own clients, bookings and marketing' },
                { on: true, text: 'Your clients and your money stay yours' },
            ],
        };
    }
    const free = (usage && usage.bailey && usage.bailey.allowance) || FREE_BAILEY_QUESTIONS_FALLBACK;
    const salon = !!(usage && usage.plan && usage.plan.type === 'salon');
    const rs = receptionistState(usage);
    const bundle = usage && usage.plan && usage.plan.bundle && usage.plan.bundle.pricePence;
    return {
        beu: [
            { on: true, text: `Bailey, ${free} questions a month` },
            salon && { on: false, text: 'Bailey for your team' },
            { on: true, text: 'Help with clients, bookings and marketing' },
        ].filter(Boolean),
        plus: [
            { on: true, text: `Bailey, up to ${cap} questions a month` },
            salon && { on: true, text: 'Bailey for your team, switched on person by person' },
            { on: true, text: 'Help with clients, bookings and marketing' },
            !rs.active && bundle ? { on: true, text: `Add the AI receptionist too: ${formatPrice(bundle)} a month for both` } : null,
        ].filter(Boolean),
    };
}

export default function UpgradeModal({ usage, isOwner, mode = 'upgrade', onClose, onGoTeam, onGoBilling, onAddCard }) {
    const [step, setStep] = useState(() => (isUpgraded(usage) || mode === 'manage' ? 'manage' : 'compare'));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [needsCard, setNeedsCard] = useState(false);
    const card = useRef(null);
    const busyRef = useRef(false);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !busyRef.current) onClose(); };
        document.addEventListener('keydown', onKey);
        const prev = document.activeElement;
        requestAnimationFrame(() => {
            const first = card.current && card.current.querySelector('button:not([disabled])');
            if (first) first.focus();
        });
        return () => { document.removeEventListener('keydown', onKey); if (prev && prev.focus) prev.focus(); };
    }, [onClose]);

    const plan = planSummary(usage);
    const solo = isSolo(usage);
    const priceLine = upgradePriceLine(usage);
    const pricePence = upgradePricePence(usage);
    const rows = compareRows(usage);
    const selfEmployed = isSelfEmployedView(usage);
    const cta = ctaState(usage, { isOwner });
    const may = cta.canUpgrade || cta.canManage;
    const salonPlan = !!(usage && usage.plan && usage.plan.type === 'salon');
    const chargeLine = upgradeChargeLine(usage);
    const up = (usage && usage.plan && usage.plan.baileyUpgrade) || {};

    const run = async (action) => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true); setError(''); setNeedsCard(false);
        try {
            const next = await pricingService.baileyUpgrade(action);
            if (next) setUsage(next);
            hapticSuccess();
            setStep(action === 'start' ? 'started' : 'cancelled');
        } catch (e) {
            hapticError();
            if (action === 'start') {
                const copy = upgradeErrorCopy(e);
                setError(copy.text);
                setNeedsCard(copy.billing);
            } else {
                setError('That didn’t go through. Try again in a moment.');
                setStep('manage');
            }
        }
        busyRef.current = false;
        setBusy(false);
    };

    let body;
    if (step === 'started') {
        body = (
            <div className="pu-done" aria-live="polite">
                <span className="pu-done-tick"><Tick size={24} /></span>
                <h2 id="pu-modal-title">You’re on {BAILEY_PLUS}</h2>
                <p>{selfEmployed ? `Ask Bailey up to ${baileyPlusCap(usage).toLocaleString('en-GB')} questions a month. It’s just for you, and your clients and money stay yours.` : `Ask Bailey up to ${baileyPlusCap(usage).toLocaleString('en-GB')} questions a month.${salonPlan ? ' You can switch Bailey on for your team in Team › Permissions.' : ''}`}</p>
                <div className="pu-modal-actions" style={{ marginTop: 10 }}>
                    <button type="button" className="pu-secondary" onClick={onClose}>Done</button>
                    {onGoTeam && salonPlan && <button type="button" className="pu-primary" onClick={() => { onClose(); onGoTeam(); }}>Choose who gets Bailey</button>}
                </div>
            </div>
        );
    } else if (step === 'cancelled') {
        body = (
            <div className="pu-done" aria-live="polite">
                <span className="pu-done-tick"><Tick size={24} /></span>
                <h2 id="pu-modal-title">{BAILEY_PLUS} is off</h2>
                <p>Bailey goes back to {FREE_BAILEY_QUESTIONS_FALLBACK} free questions a month{salonPlan ? ', and your team no longer sees Bailey' : ''}. You can come back to it any time.</p>
                <div className="pu-modal-actions" style={{ marginTop: 10 }}>
                    <button type="button" className="pu-primary" onClick={onClose}>Done</button>
                </div>
            </div>
        );
    } else if (step === 'confirm-cancel') {
        body = (
            <>
                <div className="pu-modal-head">
                    <div>
                        <h2 id="pu-modal-title">Cancel {BAILEY_PLUS}?</h2>
                        <p>Bailey goes back to {FREE_BAILEY_QUESTIONS_FALLBACK} free questions a month{salonPlan ? ', and your team no longer sees Bailey' : ''}. It stops straight away, with a credit for the rest of the month.</p>
                    </div>
                </div>
                {error && <p className="pu-inline-error" role="alert">{error}</p>}
                <div className="pu-modal-actions">
                    <button type="button" className="pu-secondary" disabled={busy} onClick={() => { setError(''); setStep('manage'); }}>Keep it</button>
                    <button type="button" className="pu-primary" style={{ background: '#B42318' }} disabled={busy} onClick={() => run('cancel')}>{busy ? 'Cancelling…' : `Cancel ${BAILEY_PLUS}`}</button>
                </div>
            </>
        );
    } else if (step === 'manage') {
        body = (
            <>
                <div className="pu-modal-head">
                    <div>
                        <h2 id="pu-modal-title">{BAILEY_PLUS}</h2>
                        <p>{up.since ? `You’ve had it since ${shortDate(up.since)}.` : `You have ${BAILEY_PLUS}.`}{priceLine ? ` ${priceLine.replace(/^\+/, '')}.` : ''}</p>
                    </div>
                    <button type="button" className="pu-close" aria-label="Close" onClick={onClose}><CloseX /></button>
                </div>
                <div className="pu-col is-pick">
                    <span className="pu-col-name">What’s included</span>
                    <ul>{rows.plus.map((r) => <Item key={r.text} on={r.on}>{r.text}</Item>)}</ul>
                </div>
                {error && <p className="pu-inline-error" role="alert">{error}</p>}
                <div className="pu-modal-actions">
                    {may
                        ? <button type="button" className="pu-link is-danger" onClick={() => { hapticTick(); setStep('confirm-cancel'); }}>Cancel {BAILEY_PLUS}</button>
                        : <p className="pu-sub" style={{ marginRight: 'auto' }}>Only the owner can change the plan.</p>}
                    <button type="button" className="pu-primary" onClick={onClose}>Done</button>
                </div>
            </>
        );
    } else {
        body = (
            <>
                <div className="pu-modal-head">
                    <div>
                        <h2 id="pu-modal-title">Get {BAILEY_PLUS}</h2>
                        <p>{selfEmployed ? `Up to ${baileyPlusCap(usage).toLocaleString('en-GB')} questions a month, just for you. Your clients and your money stay yours.` : `Up to ${baileyPlusCap(usage).toLocaleString('en-GB')} questions a month${solo ? '' : ' for this location, and Bailey for the team members you choose'}.`}</p>
                    </div>
                    <button type="button" className="pu-close" aria-label="Close" onClick={onClose}><CloseX /></button>
                </div>
                <div className="pu-compare">
                    <div className="pu-col">
                        <span className="pu-col-name">Beu</span>
                        {plan.price && <span className="pu-col-price">{plan.price}</span>}
                        <ul>{rows.beu.map((r) => <Item key={r.text} on={r.on}>{r.text}</Item>)}</ul>
                    </div>
                    <div className="pu-col is-pick">
                        <span className="pu-col-name">{BAILEY_PLUS}</span>
                        {priceLine && <span className="pu-col-price"><strong>{priceLine}</strong> on top of your plan</span>}
                        <ul>{rows.plus.map((r) => <Item key={r.text} on={r.on}>{r.text}</Item>)}</ul>
                    </div>
                </div>
                <p className="pu-sub">
                    {cta.noPlan ? unavailableCopy(usage) : chargeLine
                        ? <>{chargeLine} Cancel any time.</>
                        : <>It starts straight away. Your next bill includes a part-month charge for the rest of this month
                            {pricePence != null ? `, then ${formatPrice(pricePence)} a month${salonPlan ? ' for this location' : ''}` : ''}. Cancel any time.</>}
                </p>
                {error && (
                    <p className="pu-inline-error" role="alert">
                        {error}
                        {needsCard && (onAddCard || onGoBilling) && <>{' '}<button type="button" className="pu-link" onClick={() => { if (onAddCard) onAddCard(); else { onClose(); onGoBilling(); } }}>Add a card</button></>}
                    </p>
                )}
                <div className="pu-modal-actions">
                    <button type="button" className="pu-secondary" disabled={busy} onClick={onClose}>Not now</button>
                    {cta.noPlan ? null : may
                        ? <button type="button" className="pu-primary" disabled={busy} onClick={() => { hapticTick(); run('start'); }}>{busy ? 'Adding…' : priceLine ? `Get ${BAILEY_PLUS} · ${priceLine}` : `Get ${BAILEY_PLUS}`}</button>
                        : <p className="pu-sub">Only the owner can add this. Ask them about {BAILEY_PLUS}.</p>}
                </div>
            </>
        );
    }

    return ReactDOM.createPortal(
        <div className="pu-overlay" onClick={() => { if (!busyRef.current) onClose(); }}>
            <div className="pu-modal" ref={card} role="dialog" aria-modal="true" aria-labelledby="pu-modal-title" onClick={(e) => e.stopPropagation()}>
                {body}
            </div>
        </div>,
        document.body,
    );
}

function CloseX() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
    );
}
